/**
 * Saga Monitoring and Logging Utilities
 *
 * Provides observability and monitoring capabilities for saga executions
 */

import analyticsServiceSingleton from '../Analytics/initAnalytics'
import { AnalyticsEvent } from '../Analytics/AnalyticsEvent'
import { SagaResult } from './SagaTransaction'

export interface SagaMetrics {
  transactionId: string
  sagaType: string
  startTime: number
  endTime: number
  totalDuration: number
  success: boolean
  stepsExecuted: number
  stepsCompensated: number
  errorStep?: string
  errorMessage?: string
}

export interface SagaMonitoringOptions {
  enableAnalytics?: boolean
  enableConsoleLogging?: boolean
  enablePerformanceTracking?: boolean
  logLevel?: 'debug' | 'info' | 'warn' | 'error'
}

export class SagaMonitor {
  private options: SagaMonitoringOptions
  private static instance: SagaMonitor

  constructor(options: SagaMonitoringOptions = {}) {
    this.options = {
      enableAnalytics: true,
      enableConsoleLogging: true,
      enablePerformanceTracking: true,
      logLevel: 'info',
      ...options
    }
  }

  static getInstance(options?: SagaMonitoringOptions): SagaMonitor {
    if (!SagaMonitor.instance) {
      SagaMonitor.instance = new SagaMonitor(options)
    }
    return SagaMonitor.instance
  }

  /**
   * Create a logger function for saga transactions
   */
  createSagaLogger(sagaType: string, transactionId: string) {
    return (level: 'info' | 'warn' | 'error', message: string, data?: any) => {
      this.log(level, `[${sagaType}][${transactionId}] ${message}`, data)

      // Track errors in analytics
      if (level === 'error' && this.options.enableAnalytics) {
        analyticsServiceSingleton.trackError(new Error(message), {
          sagaType,
          transactionId,
          ...data
        })
      }
    }
  }

  /**
   * Log saga execution results
   */
  logSagaResult<T>(sagaType: string, result: SagaResult<T>): void {
    const metrics = this.extractMetrics(sagaType, result)

    if (this.options.enableConsoleLogging) {
      if (result.success) {
        this.log('info', `Saga completed successfully: ${sagaType}`, {
          transactionId: metrics.transactionId,
          duration: metrics.totalDuration,
          steps: metrics.stepsExecuted
        })
      } else {
        this.log('error', `Saga failed: ${sagaType}`, {
          transactionId: metrics.transactionId,
          duration: metrics.totalDuration,
          error: metrics.errorMessage,
          failedStep: metrics.errorStep,
          stepsCompensated: metrics.stepsCompensated
        })
      }
    }

    if (this.options.enableAnalytics) {
      this.trackSagaMetrics(metrics)
    }
  }

  /**
   * Extract metrics from saga result
   */
  private extractMetrics<T>(
    sagaType: string,
    result: SagaResult<T>
  ): SagaMetrics {
    const failedStep = result.executedSteps.find((step) => step.error)

    return {
      transactionId: result.executedSteps[0]?.stepId || 'unknown',
      sagaType,
      startTime: result.executedSteps[0]?.startTime || Date.now(),
      endTime: Date.now(),
      totalDuration: result.totalDuration,
      success: result.success,
      stepsExecuted: result.executedSteps.length,
      stepsCompensated: result.compensatedSteps.length,
      errorStep: failedStep?.stepId,
      errorMessage: failedStep?.error?.message
    }
  }

  /**
   * Track saga metrics in analytics
   */
  private trackSagaMetrics(metrics: SagaMetrics): void {
    const eventName = metrics.success
      ? AnalyticsEvent.ApiResponceSuccess
      : AnalyticsEvent.ApiResponceFailure

    analyticsServiceSingleton.trackEvent(eventName, {
      sagaType: metrics.sagaType,
      transactionId: metrics.transactionId,
      duration: metrics.totalDuration,
      stepsExecuted: metrics.stepsExecuted,
      stepsCompensated: metrics.stepsCompensated,
      success: metrics.success,
      ...(metrics.errorStep && { errorStep: metrics.errorStep }),
      ...(metrics.errorMessage && { errorMessage: metrics.errorMessage })
    })
  }

  /**
   * Internal logging method
   */
  private log(
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    data?: any
  ): void {
    if (!this.options.enableConsoleLogging) return

    const shouldLog = this.shouldLogLevel(level)
    if (!shouldLog) return

    const timestamp = new Date().toISOString()
    const logMessage = `[${timestamp}][SAGA][${level.toUpperCase()}] ${message}`

    switch (level) {
      case 'debug':
      case 'info':
        // Debug logging removed
        break
      case 'warn':
        // eslint-disable-next-line no-console
        console.warn(logMessage, data || '')
        break
      case 'error':
        // eslint-disable-next-line no-console
        console.error(logMessage, data || '')
        break
    }
  }

  /**
   * Check if a log level should be logged based on configuration
   */
  private shouldLogLevel(level: 'debug' | 'info' | 'warn' | 'error'): boolean {
    const levels = ['debug', 'info', 'warn', 'error']
    const configuredLevelIndex = levels.indexOf(this.options.logLevel || 'info')
    const currentLevelIndex = levels.indexOf(level)

    return currentLevelIndex >= configuredLevelIndex
  }
}

/**
 * Performance monitoring utilities
 */
export class SagaPerformanceMonitor {
  private static stepTimings: Map<string, number> = new Map()

  static startStepTiming(stepId: string): void {
    this.stepTimings.set(stepId, Date.now())
  }

  static endStepTiming(stepId: string): number {
    const startTime = this.stepTimings.get(stepId)
    if (!startTime) return 0

    const duration = Date.now() - startTime
    this.stepTimings.delete(stepId)
    return duration
  }

  static trackSlowStep(
    stepId: string,
    duration: number,
    threshold: number = 5000
  ): void {
    if (duration > threshold) {
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.Error, {
        type: 'SagaSlowStep',
        stepId,
        duration,
        threshold
      })
    }
  }
}

/**
 * Saga health check utilities
 */
export class SagaHealthCheck {
  private static compensationFailures: Map<string, number> = new Map()

  static recordCompensationFailure(sagaType: string): void {
    const current = this.compensationFailures.get(sagaType) || 0
    this.compensationFailures.set(sagaType, current + 1)
  }

  static getCompensationFailureCount(sagaType: string): number {
    return this.compensationFailures.get(sagaType) || 0
  }

  static checkSagaHealth(sagaType: string): {
    healthy: boolean
    compensationFailures: number
    recommendations: string[]
  } {
    const failures = this.getCompensationFailureCount(sagaType)
    const healthy = failures < 5 // Threshold for concern

    const recommendations: string[] = []
    if (failures > 3) {
      recommendations.push('Review compensation logic for reliability')
    }
    if (failures > 10) {
      recommendations.push('Consider circuit breaker pattern')
      recommendations.push('Implement manual intervention alerts')
    }

    return {
      healthy,
      compensationFailures: failures,
      recommendations
    }
  }
}

// Export default monitor instance
export const sagaMonitor = SagaMonitor.getInstance()

// Export convenience functions
export const createOrderCancellationLogger = (transactionId: string) =>
  sagaMonitor.createSagaLogger('OrderCancellation', transactionId)

export const logOrderCancellationResult = (result: SagaResult<any>) =>
  sagaMonitor.logSagaResult('OrderCancellation', result)
